/**
 * RBAC Phase 3 (item 5) — pure OIDC helpers for the admin SPA public client (`admin-console`).
 *
 * Authorization code + PKCE (S256) against auth-server. Everything here is side-effect free
 * (no `window`, no `import.meta.env`, no storage) so it is unit-tested with `tsx --test`.
 * Runtime wiring (storage, timers, redirects) lives in `oidcSession.ts`.
 */

/** The five admin resource names (token `aud` values) — phase3-token-contract.md. */
export const ADMIN_OIDC_RESOURCES = [
  'admin-api',
  'identity-verification',
  'income-verification',
  'employer-verification',
  'customer-consent',
] as const;

export const ADMIN_OIDC_SCOPE = 'openid email profile';

export const DEFAULT_ADMIN_OIDC_CLIENT_ID = 'admin-console';
export const DEFAULT_ADMIN_OIDC_REDIRECT_PATH = '/auth/callback';
export const DEFAULT_ADMIN_OIDC_POST_LOGOUT_PATH = '/login';

export type AdminAuthMode = 'password' | 'oidc';

/** `VITE_ADMIN_AUTH`: `oidc` opts in; anything else (unset, typo) keeps today's password login. */
export function parseAdminAuthMode(raw: string | null | undefined): AdminAuthMode {
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'oidc' ? 'oidc' : 'password';
}

// ── encoding / randomness ────────────────────────────────────────────────────────────────

export function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64UrlDecodeToString(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

export type RandomBytes = (n: number) => Uint8Array;

export const webCryptoRandomBytes: RandomBytes = (n) => {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
};

/** Opaque random value for `state` / `nonce` (256 bits, base64url). */
export function randomUrlSafeString(randomBytes: RandomBytes = webCryptoRandomBytes, byteLength = 32): string {
  return base64UrlEncode(randomBytes(byteLength));
}

const PKCE_VERIFIER_RE = /^[A-Za-z0-9\-._~]{43,128}$/;

/** RFC 7636 code verifier: 32 random bytes → 43 base64url chars (within the 43–128 range). */
export function generateCodeVerifier(randomBytes: RandomBytes = webCryptoRandomBytes): string {
  return base64UrlEncode(randomBytes(32));
}

export function isValidCodeVerifier(verifier: string): boolean {
  return PKCE_VERIFIER_RE.test(verifier);
}

/** S256 challenge = base64url(SHA-256(ascii(verifier))). */
export async function codeChallengeS256(verifier: string): Promise<string> {
  if (!isValidCodeVerifier(verifier)) throw new Error('Invalid PKCE code verifier');
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

// ── URL helpers ──────────────────────────────────────────────────────────────────────────

/**
 * Browser base for auth-server (`VITE_AUTH_SERVER_BASE`): a same-site path like `/auth-server`
 * (proxied) or an absolute URL. Resolved against the SPA origin; no trailing slash.
 */
export function resolveAuthServerBase(base: string | null | undefined, origin: string): string {
  const raw = (base ?? '').trim();
  if (!raw) throw new Error('VITE_AUTH_SERVER_BASE is not configured');
  const url = new URL(raw, origin);
  return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}

/** `${authServerBase}${path}` without double slashes. */
export function authServerUrl(authServerBase: string, path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${authServerBase.replace(/\/+$/, '')}${p}`;
}

/** `${origin}${path}` for redirect / post-logout URIs (must exactly match the client registration). */
export function absoluteAppUrl(origin: string, path: string): string {
  return new URL(path.startsWith('/') ? path : `/${path}`, origin).toString();
}

export type AuthorizeRequest = {
  authServerBase: string;
  clientId: string;
  redirectUri: string;
  state: string;
  nonce: string;
  codeChallenge: string;
  resources?: readonly string[];
  scope?: string;
  /** Default `mfa` — staff always need TOTP (also after SSO). */
  acrValues?: string;
  prompt?: 'none' | 'login';
  maxAge?: number;
  /** auth-server federated entry (`google` | `bizauth`). */
  provider?: 'google' | 'bizauth';
};

export function buildAuthorizeUrl(req: AuthorizeRequest): string {
  const url = new URL(authServerUrl(req.authServerBase, '/oauth/authorize'));
  const p = url.searchParams;
  p.set('response_type', 'code');
  p.set('client_id', req.clientId);
  p.set('redirect_uri', req.redirectUri);
  p.set('scope', req.scope ?? ADMIN_OIDC_SCOPE);
  p.set('state', req.state);
  p.set('nonce', req.nonce);
  p.set('code_challenge', req.codeChallenge);
  p.set('code_challenge_method', 'S256');
  for (const r of req.resources ?? ADMIN_OIDC_RESOURCES) p.append('resource', r);
  p.set('acr_values', req.acrValues ?? 'mfa');
  if (req.prompt) p.set('prompt', req.prompt);
  if (typeof req.maxAge === 'number' && Number.isFinite(req.maxAge) && req.maxAge >= 0) {
    p.set('max_age', String(Math.floor(req.maxAge)));
  }
  if (req.provider) p.set('provider', req.provider);
  return url.toString();
}

/** Kinds of authorize request the SPA makes. */
export type AuthorizeKind = 'interactive' | 'silent' | 'mfa-step-up' | 'phr-step-up' | 'provider';

/** prompt / max_age / acr_values for each kind. */
export function authorizeOptionsFor(kind: AuthorizeKind): Pick<AuthorizeRequest, 'prompt' | 'maxAge' | 'acrValues'> {
  switch (kind) {
    case 'silent':
      return { prompt: 'none', acrValues: 'mfa' };
    case 'mfa-step-up':
      return { prompt: 'login', maxAge: 0, acrValues: 'mfa' };
    // RBAC Phase 4: a fresh, phishing-resistant (passkey) sign-in for critical actions.
    case 'phr-step-up':
      return { prompt: 'login', maxAge: 0, acrValues: 'phr' };
    default:
      return { acrValues: 'mfa' };
  }
}

export function buildCodeExchangeBody(input: {
  code: string;
  redirectUri: string;
  clientId: string;
  codeVerifier: string;
}): URLSearchParams {
  return new URLSearchParams({
    grant_type: 'authorization_code',
    code: input.code,
    redirect_uri: input.redirectUri,
    client_id: input.clientId,
    code_verifier: input.codeVerifier,
  });
}

export function buildRefreshBody(input: { refreshToken: string; clientId: string }): URLSearchParams {
  return new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: input.refreshToken,
    client_id: input.clientId,
  });
}

export function buildEndSessionUrl(input: {
  authServerBase: string;
  clientId: string;
  postLogoutRedirectUri: string;
  idTokenHint?: string | null;
  state?: string;
}): string {
  const url = new URL(authServerUrl(input.authServerBase, '/oauth/end_session'));
  url.searchParams.set('client_id', input.clientId);
  url.searchParams.set('post_logout_redirect_uri', input.postLogoutRedirectUri);
  if (input.idTokenHint) url.searchParams.set('id_token_hint', input.idTokenHint);
  if (input.state) url.searchParams.set('state', input.state);
  return url.toString();
}

// ── round-trip transaction ──────────────────────────────────────────────────────────────

/** What the SPA keeps in sessionStorage for exactly one authorize → callback round trip. */
export type OidcTransaction = {
  state: string;
  nonce: string;
  codeVerifier: string;
  /** Router path to land on after the callback. */
  returnTo: string;
  kind: AuthorizeKind;
  createdAt: number;
};

/** Transactions older than this are ignored (abandoned logins). */
export const OIDC_TRANSACTION_TTL_MS = 30 * 60 * 1000;

export function parseOidcTransaction(raw: string | null | undefined, nowMs: number): OidcTransaction | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Partial<OidcTransaction>;
    if (
      typeof o.state !== 'string' || !o.state ||
      typeof o.nonce !== 'string' || !o.nonce ||
      typeof o.codeVerifier !== 'string' || !isValidCodeVerifier(o.codeVerifier) ||
      typeof o.createdAt !== 'number'
    ) {
      return null;
    }
    if (nowMs - o.createdAt > OIDC_TRANSACTION_TTL_MS || o.createdAt - nowMs > 60_000) return null;
    const kinds: AuthorizeKind[] = ['interactive', 'silent', 'mfa-step-up', 'provider'];
    return {
      state: o.state,
      nonce: o.nonce,
      codeVerifier: o.codeVerifier,
      returnTo: safeReturnTo(o.returnTo),
      kind: kinds.includes(o.kind as AuthorizeKind) ? (o.kind as AuthorizeKind) : 'interactive',
      createdAt: o.createdAt,
    };
  } catch {
    return null;
  }
}

/** Router-relative path only (`/x?y`); anything else (absolute, `//host`, `\`) → `/`. */
export function safeReturnTo(raw: unknown): string {
  if (typeof raw !== 'string') return '/';
  const t = raw.trim();
  if (!t.startsWith('/') || t.startsWith('//') || t.includes('\\')) return '/';
  if (t === '/auth/callback' || t.startsWith('/auth/callback?') || t === '/login' || t.startsWith('/login?')) return '/';
  return t;
}

// ── callback ─────────────────────────────────────────────────────────────────────────────

export type CallbackParams =
  | { kind: 'code'; code: string; state: string | null }
  | { kind: 'error'; error: string; errorDescription: string | null; state: string | null }
  | { kind: 'invalid' };

export function parseCallbackParams(search: string): CallbackParams {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const state = p.get('state');
  const error = p.get('error');
  if (error) return { kind: 'error', error, errorDescription: p.get('error_description'), state };
  const code = p.get('code');
  if (code) return { kind: 'code', code, state };
  return { kind: 'invalid' };
}

export type CallbackErrorAction = 'interactive' | 'show-error';

/**
 * A failed silent (`prompt=none`) authorize falls back to a top-level interactive authorize:
 * `login_required` lands on the login page, `interaction_required` (MFA pending) on its TOTP step
 * (`acr_values=mfa` is always sent). Anything else — or a failure of a non-silent attempt — is
 * shown, never retried (no redirect loops).
 */
export function decideCallbackError(error: string, kind: AuthorizeKind): CallbackErrorAction {
  if (kind === 'silent' && (error === 'login_required' || error === 'interaction_required' || error === 'consent_required')) {
    return 'interactive';
  }
  return 'show-error';
}

// ── tokens ───────────────────────────────────────────────────────────────────────────────

export type TokenResponse = {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  id_token?: string;
  scope?: string;
};

export function parseTokenResponse(body: unknown): TokenResponse | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  if (typeof o.access_token !== 'string' || !o.access_token) return null;
  if (typeof o.token_type !== 'string' || o.token_type.toLowerCase() !== 'bearer') return null;
  return {
    access_token: o.access_token,
    token_type: o.token_type,
    expires_in: typeof o.expires_in === 'number' ? o.expires_in : undefined,
    refresh_token: typeof o.refresh_token === 'string' && o.refresh_token ? o.refresh_token : undefined,
    id_token: typeof o.id_token === 'string' && o.id_token ? o.id_token : undefined,
    scope: typeof o.scope === 'string' ? o.scope : undefined,
  };
}

/** Decode a JWS payload WITHOUT verifying it (signature trust is the resource servers' job). */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const parsed = JSON.parse(base64UrlDecodeToString(parts[1])) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export type IdTokenCheck = { ok: true; claims: Record<string, unknown> } | { ok: false; reason: string };

/**
 * Client-side id_token checks (OIDC Core §3.1.3.7 subset): iss exact match (when the issuer is
 * known), aud contains client_id (azp when multi-aud), nonce equals the transaction nonce, not
 * expired (with skew). `expectedNonce: null` (refresh grant) skips the nonce check.
 */
export function validateIdTokenClaims(
  idToken: string,
  expected: { issuer: string | null; clientId: string; nonce: string | null; nowSec: number; skewSec?: number },
): IdTokenCheck {
  const claims = decodeJwtPayload(idToken);
  if (!claims) return { ok: false, reason: 'id_token_malformed' };
  const skew = expected.skewSec ?? 120;
  if (expected.issuer && claims.iss !== expected.issuer) return { ok: false, reason: 'id_token_iss_mismatch' };
  const aud = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
  if (!aud.includes(expected.clientId)) return { ok: false, reason: 'id_token_aud_mismatch' };
  if (aud.length > 1 && claims.azp !== undefined && claims.azp !== expected.clientId) {
    return { ok: false, reason: 'id_token_azp_mismatch' };
  }
  if (expected.nonce !== null && claims.nonce !== expected.nonce) return { ok: false, reason: 'id_token_nonce_mismatch' };
  if (typeof claims.exp !== 'number' || claims.exp + skew < expected.nowSec) return { ok: false, reason: 'id_token_expired' };
  if (typeof claims.sub !== 'string' || !claims.sub) return { ok: false, reason: 'id_token_sub_missing' };
  return { ok: true, claims };
}

/** Access-token expiry (epoch s): its `exp` claim, else `now + expires_in`, else null. */
export function accessTokenExpiry(accessToken: string, expiresIn: number | undefined, nowSec: number): number | null {
  const claims = decodeJwtPayload(accessToken);
  if (claims && typeof claims.exp === 'number') return claims.exp;
  if (typeof expiresIn === 'number' && expiresIn > 0) return nowSec + expiresIn;
  return null;
}

/**
 * Delay (ms) before refreshing: at `exp - lead`, lead = max(60s, 10% of the lifetime) capped at
 * half the lifetime; never sooner than `minDelayMs`. Already-expired tokens refresh right away.
 */
export function computeRefreshDelayMs(input: {
  expSec: number;
  nowMs: number;
  issuedAtSec?: number | null;
  minDelayMs?: number;
}): number {
  const minDelay = input.minDelayMs ?? 5_000;
  const nowSec = input.nowMs / 1000;
  const lifetime = Math.max(0, input.expSec - (input.issuedAtSec ?? nowSec));
  const lead = Math.min(Math.max(60, lifetime * 0.1), lifetime > 0 ? lifetime / 2 : 60);
  const delayMs = Math.floor((input.expSec - lead - nowSec) * 1000);
  if (input.expSec <= nowSec) return 0;
  return Math.max(minDelay, delayMs);
}

/** Should the access token be treated as unusable now (expired or about to be)? */
export function isAccessTokenStale(expSec: number | null, nowMs: number, leewaySec = 10): boolean {
  if (expSec === null) return false;
  return expSec - leewaySec <= nowMs / 1000;
}

// ── API failure → action ─────────────────────────────────────────────────────────────────

export type OidcApiFailureAction =
  /** refresh once, then re-send the request */
  | 'refresh'
  /** top-level authorize (session gone) */
  | 'reauthorize'
  /** top-level authorize with max_age=0&prompt=login&acr_values=mfa */
  | 'mfa-step-up'
  /** MFA step-up was just tried and the API still says MFA_REQUIRED — show the banner */
  | 'banner'
  /** top-level authorize with max_age=0&prompt=login&acr_values=phr (passkey) */
  | 'phr-step-up'
  | 'ignore';

export const MFA_REQUIRED_ERROR_CODE = 'MFA_REQUIRED';
/** admin-api: a critical key needs a passkey session (RBAC Phase 4). */
export const PHR_REQUIRED_ERROR_CODE = 'PHR_REQUIRED';

/** A redirect we made within this window is not repeated (loop guard). */
export const OIDC_REDIRECT_LOOP_WINDOW_MS = 2 * 60 * 1000;

export function decideOidcApiFailure(input: {
  status: number | undefined;
  code: unknown;
  /** The request already went through one refresh + retry. */
  refreshAttempted: boolean;
  /** A refresh token is held in memory. */
  hasRefreshToken: boolean;
  nowMs: number;
  /** Last top-level re-authorize (ms epoch) for a 401, or null. */
  lastReauthorizeAtMs: number | null;
  /** Last MFA step-up authorize (ms epoch), or null. */
  lastMfaStepUpAtMs: number | null;
  /** Last passkey (phr) step-up authorize (ms epoch), or null. */
  lastPhrStepUpAtMs?: number | null;
}): OidcApiFailureAction {
  const recent = (t: number | null) => t !== null && input.nowMs - t >= 0 && input.nowMs - t < OIDC_REDIRECT_LOOP_WINDOW_MS;
  if (input.status === 401) {
    if (!input.refreshAttempted && input.hasRefreshToken) return 'refresh';
    return recent(input.lastReauthorizeAtMs) ? 'ignore' : 'reauthorize';
  }
  if (input.status === 403 && input.code === MFA_REQUIRED_ERROR_CODE) {
    return recent(input.lastMfaStepUpAtMs) ? 'banner' : 'mfa-step-up';
  }
  // A passkey step-up that just happened and still fails is left to the page (its error alert says
  // a passkey is needed) rather than redirecting again.
  if (input.status === 403 && input.code === PHR_REQUIRED_ERROR_CODE) {
    return recent(input.lastPhrStepUpAtMs ?? null) ? 'ignore' : 'phr-step-up';
  }
  return 'ignore';
}

// ── auth-server login page (login_url) ──────────────────────────────────────────────────

export type AuthServerLoginParams = {
  /** auth-server `redirect` (absolute authorize URL), validated. */
  redirect: string;
  mfa: boolean;
  mfaEnroll: boolean;
  /** RBAC Phase 4: `phr=1` — the session needs a passkey (sign in or step up with one). */
  phr: boolean;
};

/**
 * `/login?redirect=<authorize url>[&mfa=1][&mfa_enroll=1]` from auth-server. The redirect must
 * point at an `/oauth/authorize` endpoint on the auth-server base origin, the issuer origin or the
 * SPA origin (same-site proxy) — anything else is ignored (open-redirect guard).
 */
export function parseAuthServerLoginParams(
  search: string,
  allowed: { authServerBase: string; issuer?: string | null; appOrigin: string },
): AuthServerLoginParams | null {
  const p = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = p.get('redirect');
  if (!raw) return null;
  const redirect = validateAuthorizeRedirect(raw, allowed);
  if (!redirect) return null;
  return { redirect, mfa: p.get('mfa') === '1', mfaEnroll: p.get('mfa_enroll') === '1', phr: p.get('phr') === '1' };
}

function originOf(u: string | null | undefined, base?: string): string | null {
  if (!u) return null;
  try {
    return new URL(u, base).origin;
  } catch {
    return null;
  }
}

export function validateAuthorizeRedirect(
  raw: string,
  allowed: { authServerBase: string; issuer?: string | null; appOrigin: string },
): string | null {
  let url: URL;
  try {
    url = new URL(raw, allowed.authServerBase);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!/\/oauth\/authorize$/.test(url.pathname)) return null;
  const origins = new Set(
    [originOf(allowed.authServerBase), originOf(allowed.issuer ?? null), originOf(allowed.appOrigin)].filter(
      (o): o is string => !!o,
    ),
  );
  return origins.has(url.origin) ? url.toString() : null;
}

/**
 * auth-server returns `redirectTo` relative to its own mount (e.g. `/oauth/authorize?...`). The
 * browser must follow it through `VITE_AUTH_SERVER_BASE` so the auth-server session cookie (set
 * on that base) goes with it. A path that already starts with the base path is not prefixed twice.
 * Absolute URLs are accepted only on the auth-server base origin.
 */
export function resolveAuthServerRedirect(redirectTo: string | null | undefined, authServerBase: string): string | null {
  if (typeof redirectTo !== 'string') return null;
  const t = redirectTo.trim();
  if (!t) return null;
  const base = new URL(authServerBase);
  const basePath = base.pathname.replace(/\/+$/, '');
  if (/^https?:\/\//i.test(t)) {
    try {
      const u = new URL(t);
      return u.origin === base.origin ? u.toString() : null;
    } catch {
      return null;
    }
  }
  if (!t.startsWith('/') || t.startsWith('//') || t.includes('\\')) return null;
  const path = basePath && (t === basePath || t.startsWith(`${basePath}/`) || t.startsWith(`${basePath}?`)) ? t : `${basePath}${t}`;
  return `${base.origin}${path}`;
}

/** auth-server JSON error body → a user-facing message. */
export function authServerErrorMessage(status: number, body: unknown, fallback: string): string {
  const o = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  const msg = [o.message, o.error_description, o.error].find((v) => typeof v === 'string' && v.trim()) as string | undefined;
  if (status === 401 && /invalid totp|invalid recovery/i.test(msg ?? '')) {
    return msg!.toLowerCase().includes('recovery')
      ? 'That recovery code is not valid. Check it and try again.'
      : 'That authenticator code is not valid. Wait for a fresh code and try again.';
  }
  if (status === 401 && /not logged in|not authenticated|setup not initiated/i.test(msg ?? '')) {
    return 'Your sign-in expired. Start again.';
  }
  if (status === 429) return msg || 'Too many attempts. Wait a moment and try again.';
  return msg || fallback;
}

/** Which step the auth-server login page starts on. */
/** `passkey`: confirm with a passkey for acr_values=phr (RBAC Phase 4). */
export type LoginPageStep = 'credentials' | 'mfa' | 'mfa-enroll' | 'passkey';

export function initialLoginStep(params: Pick<AuthServerLoginParams, 'mfa' | 'mfaEnroll'> & { phr?: boolean }): LoginPageStep {
  if (params.mfaEnroll) return 'mfa-enroll';
  if (params.mfa) return 'mfa';
  if (params.phr) return 'passkey';
  return 'credentials';
}
